import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { get } from './apiClient';
import {
  Container, Card, CardHeader, CardContent,
  Grid, Box, CircularProgress, Alert, Chip, List, ListItem, ListItemText,
  Typography
} from '@mui/material';

// The clock-skew finding requires a delay floor; one-shot refetch could leave a still-running page stale.
const MIN_EXPIRY_REFETCH_DELAY_MS = 15_000;

// Helper to format Firestore Timestamps and date strings
const formatDate = (timestamp) => {
  if (timestamp && typeof timestamp.seconds === 'number') {
    return new Date(timestamp.seconds * 1000).toLocaleString();
  }
  if (typeof timestamp === 'string') {
    const date = new Date(timestamp);
    if (!isNaN(date)) {
      return date.toLocaleString();
    }
  }
  return 'N/A';
};

const StatusChip = ({ status }) => {
  let color = 'default';
  switch (status) {
    case 'completed':
      color = 'success';
      break;
    case 'failed':
      color = 'error';
      break;
    case 'running':
      color = 'info';
      break;
    default:
      color = 'secondary';
  }
  return <Chip label={status} color={color} />;
};

export default function JobDetailsPage() {
  const { jobId } = useParams();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    let expiryTimerId;

    // An expiry refetch is required by the stale-lease finding; the jobId-only fetch cannot update with the clock.
    const fetchJob = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await get(`/jobs/${jobId}`);
        if (!active) return;

        setJob(data.job);
        if (data.job.status === 'running') {
          const expiresAt = Date.parse(data.job.leaseExpiresAt);
          if (Number.isFinite(expiresAt)) {
            clearTimeout(expiryTimerId);
            expiryTimerId = setTimeout(
              fetchJob,
              Math.max(MIN_EXPIRY_REFETCH_DELAY_MS, expiresAt - Date.now() + 1)
            );
          }
        }
      } catch (err) {
        if (!active) return;
        setError('Failed to fetch job details.');
        console.error(err);
      } finally {
        if (active) setLoading(false);
      }
    };
    fetchJob();

    return () => {
      active = false;
      clearTimeout(expiryTimerId);
    };
  }, [jobId]);

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}><CircularProgress /></Box>}
      {error && <Alert severity="error">{error}</Alert>}
      
      {job && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardHeader title="Job Summary" />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body1"><strong>URL:</strong> {job.url}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body1"><strong>Category:</strong> {job.product_category}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body1"><strong>Created:</strong> {formatDate(job.createdAt)}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body1"><strong>Completed:</strong> {formatDate(job.completedAt)}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body1"><strong>Status:</strong></Typography>
                      <StatusChip status={job.status} />
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            {job.status === 'completed' && job.result ? (
              <Card>
                <CardHeader title="Queries" />
                <CardContent>
                  <List dense>
                    {job.result.queries.map((q, i) => <ListItem key={i}><ListItemText primary={`${i + 1}. ${q}`} /></ListItem>)}
                  </List>
                </CardContent>
              </Card>
            ) : job.status === 'failed' ? (
              <Alert severity="error">
                <strong>Job Failed:</strong> The job failed to complete. The error message is below:
                <pre><code>{job.error}</code></pre>
              </Alert>
            ) : (
              <Alert severity="info">Job is not yet complete. Results will be shown here once the job is finished.</Alert>
            )}
          </Grid>
        </Grid>
      )}
    </Container>
  );
}
